'use client';

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Grid, Box, Typography, CircularProgress, Alert, FormControl, Select, MenuItem, InputLabel, SelectChangeEvent } from '@mui/material';
import { Venue, VenueBookedTimeSlot } from '../../../shared/models';
import {
    MobileVerticalFeaturedCard,
    MobileVerticalFeaturedCardSkeletonGrid
} from './mobile_vertical_feature_card';
import { trackEvent } from '../lib/analytics';

// CSS for staggered animation (only on desktop)
const fadeInAnimation = `
@media (min-width: 769px) {
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .venue-card {
    opacity: 0;
    animation: fadeIn 0.3s ease-out forwards;
  }
}

@media (max-width: 768px) {
  .venue-card {
    opacity: 1;
  }
}
`;

interface VenueCardListProps {
    venues: Venue[];
    allVenues: Venue[];
    venueBookedTimeSlots: Record<string, Record<string, VenueBookedTimeSlot[]>>;
    currentDate: Date;
    people?: string;
    selectedTime?: string;
    onVenueHover: (venueId: string | null) => void;
    onCenterMap?: (venue: Venue) => void;
    isLoading?: boolean;
    isPending?: boolean;
    isLoadingFullResults?: boolean;
    isMapFiltered?: boolean;
    visibleVenueIds?: string[];
    venuePrices?: Record<string, number> | null;
    privacyLevel?: 'private' | 'semi-private' | 'non-private';
}

// Get venue price from backend data or fallback, considering privacy level
const getVenuePrice = (venue: Venue, venuePrices: Record<string, number> | null, people: number | null = 10, privacyLevel?: 'private' | 'semi-private' | 'non-private'): number => {
    // Check if venue has a no minimum spend option for this party size and privacy level
    const hasNoMinimumSpendOption = Object.entries(venue.PricingRatesPerSection).some(
        ([sectionId, rate]) => {
            const section = venue.Sections[sectionId];
            const matchesPrivacyLevel = !privacyLevel || section?.PrivacyLevel === privacyLevel;
            return rate?.IsNoMinimumSpendOption === true &&
                section?.Capacity >= (people || 10) &&
                matchesPrivacyLevel;
        }
    );

    // If venue has no minimum spend option, return 0 to make it appear first in low-to-high sort
    if (hasNoMinimumSpendOption) {
        return 0;
    }

    // If we have pricing data from backend, use it
    if (venuePrices && venuePrices[venue.ID] !== undefined) {
        // If the price is 0 and a privacy level is specified, it means this venue
        // doesn't have a matching section, so return a high price to sort it last
        if (venuePrices[venue.ID] === 0 && privacyLevel) {
            return Number.MAX_SAFE_INTEGER;
        }
        return venuePrices[venue.ID];
    }
    // Fallback: Use a default high value if no price is available
    return Number.MAX_SAFE_INTEGER;
};

// Add a Intersection Observer for tracking venue card impressions
const VenueCardWithImpressionTracking = React.memo(({
    venue,
    venueBookedTimeSlots,
    currentDate,
    people,
    selectedTime,
    onVenueHover,
    onCenterMap,
    venuePrices,
    privacyLevel
}: {
    venue: Venue;
    venueBookedTimeSlots: Record<string, Record<string, VenueBookedTimeSlot[]>>;
    currentDate: Date;
    people: number;
    selectedTime: string | null;
    onVenueHover: (venueId: string | null) => void;
    onCenterMap?: (venue: Venue) => void;
    venuePrices?: Record<string, number> | null;
    privacyLevel?: 'private' | 'semi-private' | 'non-private';
}) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const hasTrackedImpression = useRef<boolean>(false);

    // Track venue card click
    const handleVenueClick = useCallback(() => {
        trackEvent('venue_card_click', {
            venueId: venue.ID,
            venueName: venue.Name,
            date: currentDate.toISOString().split('T')[0],
            people: people,
            time: selectedTime || 'not-set'
        });
    }, [venue, currentDate, people, selectedTime]);

    return (
        <div ref={cardRef}>
            <MobileVerticalFeaturedCard
                venue={venue}
                venueBookedTimeSlots={venueBookedTimeSlots[venue.ID] || {}}
                currentDate={currentDate}
                people={people}
                onHover={onVenueHover}
                onCenterMap={onCenterMap}
                venuePrices={venuePrices}
                onClick={handleVenueClick}
                privacyLevel={privacyLevel}
            />
        </div>
    );
});

VenueCardWithImpressionTracking.displayName = 'VenueCardWithImpressionTracking';

// Replace VenueCard with VenueCardWithImpressionTracking in the existing code
const VenueCard = VenueCardWithImpressionTracking;

const VenueCardList: React.FC<VenueCardListProps> = ({
    venues,
    allVenues,
    venueBookedTimeSlots,
    currentDate,
    people,
    selectedTime,
    onVenueHover,
    onCenterMap,
    isLoading = false,
    isPending = false,
    isLoadingFullResults = false,
    isMapFiltered = false,
    visibleVenueIds = [],
    venuePrices = null,
    privacyLevel
}) => {
    // State
    const [sortOption, setSortOption] = useState<string>('default');
    const [visibleItems, setVisibleItems] = useState<number>(6); // Reduced initial batch size for mobile
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loadingTriggerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const isMobileRef = useRef<boolean>(false);
    const loadStartTimeRef = useRef<number>(0);
    const lastScrollPositionRef = useRef<number>(0);
    const hasSetupMobileScrollRef = useRef<boolean>(false);

    // Check if we're on mobile
    useEffect(() => {
        const checkMobile = () => {
            const wasMobile = isMobileRef.current;
            isMobileRef.current = window.innerWidth <= 768;

            // If mobile status changed, reset scroll handlers
            if (wasMobile !== isMobileRef.current) {
                hasSetupMobileScrollRef.current = false;
                setupScrollHandlers();
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Setup proper scroll handlers based on device type
    const setupScrollHandlers = useCallback(() => {
        // Remove any existing handlers
        if (isMobileRef.current) {
            // For mobile - use window scroll
            if (!hasSetupMobileScrollRef.current) {
                window.addEventListener('scroll', handleMobileScroll);
                hasSetupMobileScrollRef.current = true;
            }

            // Remove desktop handlers if they exist
            if (scrollContainerRef.current) {
                scrollContainerRef.current.removeEventListener('scroll', handleDesktopScroll);
            }
        } else {
            // For desktop - use container scroll
            window.removeEventListener('scroll', handleMobileScroll);
            hasSetupMobileScrollRef.current = false;

            if (scrollContainerRef.current) {
                scrollContainerRef.current.addEventListener('scroll', handleDesktopScroll);
            }
        }
    }, []);

    // Handle sort option change with analytics tracking
    const handleSortChange = (event: SelectChangeEvent) => {
        const newSortOption = event.target.value;
        setSortOption(newSortOption);

        // Track sort change
        trackEvent('venue_list_sort', {
            sortOption: newSortOption,
            venuesCount: venues.length,
            date: currentDate.toISOString().split('T')[0],
            time: selectedTime || 'not-set',
            people: people || 'not-set'
        });

        // Reset visible items when sorting changes
        setVisibleItems(isMobileRef.current ? 6 : 12);
        // Scroll back to top
        if (isMobileRef.current) {
            window.scrollTo({ top: 0, behavior: 'auto' });
        } else if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    };

    // Apply sorting to venues with memoization
    const sortedVenues = useMemo(() => {
        if (!venues || venues.length === 0) return [];

        // Create a copy to avoid mutating the original array
        let venuesCopy = [...venues];

        let sorted;
        switch (sortOption) {
            case 'price-low-high':
                sorted = venuesCopy.sort((a, b) => {
                    const priceA = getVenuePrice(a, venuePrices, people ? parseInt(people) : null, privacyLevel);
                    const priceB = getVenuePrice(b, venuePrices, people ? parseInt(people) : null, privacyLevel);
                    return priceA - priceB;
                });
                break;
            case 'price-high-low':
                sorted = venuesCopy.sort((a, b) => {
                    const priceA = getVenuePrice(a, venuePrices, people ? parseInt(people) : null, privacyLevel);
                    const priceB = getVenuePrice(b, venuePrices, people ? parseInt(people) : null, privacyLevel);
                    return priceB - priceA;
                });
                break;
            default:
                sorted = venuesCopy;
        }

        return sorted;
    }, [venues, sortOption, venuePrices, people, privacyLevel]);

    // Calculate displayed venues whenever filters or map bounds change
    const displayedVenues = useMemo(() => {
        return isMapFiltered && visibleVenueIds.length > 0
            ? sortedVenues.filter(venue => visibleVenueIds.includes(venue.ID))
            : sortedVenues;
    }, [sortedVenues, isMapFiltered, visibleVenueIds]);

    // Memoize alternate venue list
    const alternateVenues = useMemo(() => {
        // First filter venues that aren't in the main venues list
        let filteredVenues = allVenues
            .filter(v => !venues.some(venue => venue.ID === v.ID));

        // Then filter by map visibility if map filtering is enabled
        if (isMapFiltered && visibleVenueIds.length > 0) {
            filteredVenues = filteredVenues.filter(venue =>
                visibleVenueIds.includes(venue.ID)
            );
        }

        // Limit to 6 venues
        return filteredVenues.slice(0, 6);
    }, [allVenues, venues, isMapFiltered, visibleVenueIds]);

    // Check if we have more venues to show
    const hasMoreVenues = visibleItems < displayedVenues.length;

    // Enhanced triggerLoadMore function with analytics tracking
    const triggerLoadMore = useCallback(() => {
        if (isLoadingMore || !hasMoreVenues) return;

        setIsLoadingMore(true);
        loadStartTimeRef.current = performance.now();

        // Use setTimeout to simulate a small delay (could be removed in production)
        setTimeout(() => {
            // Increase visible items (smaller batch size on mobile)
            setVisibleItems(prev => prev + (isMobileRef.current ? 6 : 12));
            setIsLoadingMore(false);
        }, 800);
    }, [isLoadingMore, hasMoreVenues]);

    // Enhanced handleLoadMoreClick with analytics tracking
    const handleLoadMoreClick = useCallback(() => {
        triggerLoadMore();
    }, [triggerLoadMore]);

    // Mobile-specific scroll handler
    const handleMobileScroll = useCallback(() => {
        if (isLoadingMore || !hasMoreVenues || !isMobileRef.current) return;

        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        // Calculate how far from bottom (in pixels)
        const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

        // Load more when within 500px of bottom on mobile
        if (distanceFromBottom < 500) {
            triggerLoadMore();
        }
    }, [isLoadingMore, hasMoreVenues, triggerLoadMore]);

    // Handle scroll for desktop only - using normal event listeners
    const handleDesktopScroll = useCallback(() => {
        if (isLoadingMore || !hasMoreVenues || isMobileRef.current) return;

        if (scrollContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 800;

            // Save the last scroll position
            lastScrollPositionRef.current = scrollTop;

            // Load more if scrolled near the bottom
            if (scrolledToBottom) {
                triggerLoadMore();
            }
        }
    }, [isLoadingMore, hasMoreVenues, triggerLoadMore]);

    // Setup Intersection Observer for infinite scroll
    useEffect(() => {
        // Cleanup previous observer if it exists
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // Create new observer that triggers loading when loading element is visible
        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && hasMoreVenues && !isLoadingMore) {
                    triggerLoadMore();
                }
            },
            {
                // More aggressive threshold for mobile to ensure loading triggers
                rootMargin: isMobileRef.current ? '0px 0px 800px 0px' : '0px 0px 300px 0px',
                threshold: 0.1,
            }
        );

        // Observe the loading trigger element if it exists
        if (loadingTriggerRef.current) {
            observerRef.current.observe(loadingTriggerRef.current);
        }

        // Cleanup on unmount
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMoreVenues, isLoadingMore, triggerLoadMore]);

    // Set up scroll listeners
    useEffect(() => {
        setupScrollHandlers();

        return () => {
            // Clean up scroll listeners
            if (scrollContainerRef.current) {
                scrollContainerRef.current.removeEventListener('scroll', handleDesktopScroll);
            }
            window.removeEventListener('scroll', handleMobileScroll);
        };
    }, [handleDesktopScroll, handleMobileScroll, setupScrollHandlers]);

    // Reset visible items when venues or filters change
    useEffect(() => {
        const isMobile = isMobileRef.current;
        setVisibleItems(isMobile ? 6 : 12);

        // Reset scroll tracking
        lastScrollPositionRef.current = 0;

        // Reset scroll position
        if (isMobile) {
            window.scrollTo({ top: 0, behavior: 'auto' });
        } else if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [venues, isMapFiltered, visibleVenueIds, sortOption]);

    // For alternate venue cards - only render first 3 on mobile
    const renderAlternateVenueCards = useCallback(() => {
        const isMobile = isMobileRef.current;
        const venuesToShow = isMobile ? alternateVenues.slice(0, 3) : alternateVenues;

        return venuesToShow.map((venue, index) => (
            <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={4}
                key={`alt-${venue.ID || index}`}
                className="venue-card"
                sx={{
                    animationDelay: isMobile ? '0s' : `${index * 0.05}s`,
                    minHeight: { xs: '240px', md: '280px' },
                    padding: { xs: '4px', md: '8px' },
                }}
            >
                <VenueCard
                    venue={venue}
                    venueBookedTimeSlots={venueBookedTimeSlots}
                    currentDate={currentDate}
                    people={people ? parseInt(people) : 10}
                    selectedTime={selectedTime}
                    onVenueHover={onVenueHover}
                    onCenterMap={onCenterMap}
                    venuePrices={venuePrices}
                    privacyLevel={privacyLevel}
                />
            </Grid>
        ));
    }, [alternateVenues, venueBookedTimeSlots, currentDate, people, selectedTime, onVenueHover, onCenterMap, venuePrices, privacyLevel]);

    // Render main venues grid
    const renderVenueCards = useMemo(() => {
        const visibleVenues = displayedVenues.slice(0, visibleItems);
        const isMobile = isMobileRef.current;


        return visibleVenues.map((venue, index) => (
            <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={4}
                key={`${venue.ID || index}-${sortOption}`}
                className="venue-card"
                sx={{
                    animationDelay: isMobile ? '0s' : `${index * 0.05}s`,
                    minHeight: { xs: '240px', md: '280px' },
                    padding: { xs: '4px', md: '8px' },
                }}
            >
                <VenueCard
                    venue={venue}
                    venueBookedTimeSlots={venueBookedTimeSlots}
                    currentDate={currentDate}
                    people={people ? parseInt(people) : 10}
                    selectedTime={selectedTime}
                    onVenueHover={onVenueHover}
                    onCenterMap={onCenterMap}
                    venuePrices={venuePrices}
                    privacyLevel={privacyLevel}
                />
            </Grid>
        ));
    }, [displayedVenues, visibleItems, sortOption, venueBookedTimeSlots, currentDate, people, selectedTime, onVenueHover, onCenterMap, venuePrices, privacyLevel]);

    if (isLoading || isPending) {
        return (
            <Box sx={{ padding: '0 8px 16px 16px' }}>
                <MobileVerticalFeaturedCardSkeletonGrid count={isMobileRef.current ? 3 : 6} />
            </Box>
        );
    }

    if (displayedVenues.length === 0) {
        return (
            <Box sx={{ padding: '0 8px 16px 16px' }}>
                {isMapFiltered && visibleVenueIds.length === 0 ? (
                    <Alert
                        severity="info"
                        sx={{
                            backgroundColor: 'rgba(224, 177, 241, 0.1)',
                            color: '#C0C0DD',
                            '.MuiAlert-icon': {
                                color: '#C0C0DD'
                            },
                            marginBottom: 3
                        }}
                    >
                        No venues are visible in the current map view. Try zooming out or panning the map.
                    </Alert>
                ) : (
                    <>
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#C0C0DD',
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                mb: 3
                            }}
                        >
                            No venues match your current search criteria. Try adjusting your filters.
                        </Typography>

                        {/* Show alternative venues even when no primary venues match */}
                        {alternateVenues.length > 0 && (
                            <Box sx={{
                                mt: 3,
                                mb: 3,
                                borderTop: '1px solid rgba(255, 255, 255, 0.12)',
                                pt: 4
                            }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#C0C0DD',
                                        fontFamily: 'Inter',
                                        fontWeight: 500,
                                        mb: 3
                                    }}
                                >
                                    Here are some other available options you might like:
                                </Typography>
                                <Grid container spacing={{ xs: 2.0, md: 2.8 }} paddingRight={0}>
                                    {renderAlternateVenueCards()}
                                </Grid>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ padding: '0 0 16px 0' }}>
            {/* Inject CSS animation styles */}
            <style dangerouslySetInnerHTML={{ __html: fadeInAnimation }} />

            {/* Add sorting options */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                mb: 3
            }}>
                {isMapFiltered && venues.length < allVenues.length * 0.5 && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#C0C0DD',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 12px',
                            borderRadius: '16px',
                            backgroundColor: 'rgba(224, 177, 241, 0.08)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        {displayedVenues.length} venues in map view
                    </Typography>
                )}
                <FormControl
                    size="small"
                    sx={{
                        minWidth: 180,
                        '& .MuiOutlinedInput-root': {
                            color: '#C0C0DD',
                            backgroundColor: 'rgba(224, 177, 241, 0.08)',
                            borderRadius: '24px',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            border: 'none',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                            '& fieldset': {
                                borderColor: 'transparent',
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(224, 177, 241, 0.12)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
                            },
                            '&.Mui-focused': {
                                backgroundColor: 'rgba(224, 177, 241, 0.16)',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.16)',
                                '& fieldset': {
                                    borderColor: 'rgba(224, 177, 241, 0.5)',
                                },
                            }
                        },
                        '& .MuiSelect-select': {
                            paddingLeft: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        },
                        '& .MuiSelect-icon': {
                            color: '#C0C0DD',
                            right: '12px',
                        }
                    }}
                >
                    <Select
                        id="sort-select"
                        value={sortOption}
                        onChange={handleSortChange}
                        displayEmpty
                        renderValue={(value) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="21" y1="10" x2="3" y2="10"></line>
                                    <line x1="21" y1="6" x2="9" y2="6"></line>
                                    <line x1="21" y1="14" x2="7" y2="14"></line>
                                    <line x1="21" y1="18" x2="11" y2="18"></line>
                                </svg>
                                {value === 'default' ? 'Recommended' :
                                    value === 'price-low-high' ? 'Price: Low to High' :
                                        value === 'price-high-low' ? 'Price: High to Low' : 'Sort By'}
                            </Box>
                        )}
                        MenuProps={{
                            PaperProps: {
                                elevation: 3,
                                sx: {
                                    backgroundColor: '#1C1C34',
                                    color: '#C0C0DD',
                                    borderRadius: '12px',
                                    marginTop: '8px',
                                    overflow: 'hidden',
                                    '& .MuiMenuItem-root': {
                                        fontSize: '0.875rem',
                                        padding: '10px 16px',
                                        transition: 'background-color 0.15s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(224, 177, 241, 0.1)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(224, 177, 241, 0.2)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(224, 177, 241, 0.3)',
                                            }
                                        }
                                    }
                                }
                            }
                        }}
                    >
                        <MenuItem value="default">Recommended</MenuItem>
                        <MenuItem value="price-low-high">Price: Low to High</MenuItem>
                        <MenuItem value="price-high-low">Price: High to Low</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Main scrollable container that contains everything */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    overflow: 'auto',
                    willChange: 'transform', // Optimize for GPU acceleration
                    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                    transform: 'translateZ(0)', // Force GPU acceleration
                    backfaceVisibility: 'hidden', // Reduce visual glitches
                }}
            >
                <Box>
                    {/* Main Venue Cards */}
                    <Grid container spacing={{ xs: 2.0, md: 2.8 }} paddingRight={0}>
                        {renderVenueCards}
                    </Grid>

                    {/* Loading indicator at bottom - This element also serves as the IntersectionObserver trigger */}
                    {hasMoreVenues && (
                        <Box
                            ref={loadingTriggerRef}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 3,
                                // Make sure this element is tall enough to be visible
                                minHeight: isMobileRef.current ? '150px' : '100px',
                            }}
                        >
                            {isLoadingMore ? (
                                <CircularProgress size={24} thickness={4} sx={{ color: '#C0C0DD' }} />
                            ) : (
                                <Box
                                    onClick={handleLoadMoreClick}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(224, 177, 241, 0.1)',
                                        color: '#C0C0DD',
                                        py: 1.5,
                                        px: 3,
                                        borderRadius: 28,
                                        fontSize: '0.9rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(224, 177, 241, 0.15)',
                                            boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                                        },
                                        '&:active': {
                                            backgroundColor: 'rgba(224, 177, 241, 0.2)',
                                            transform: 'translateY(1px)',
                                        }
                                    }}
                                >
                                    Show More Venues ({displayedVenues.length - visibleItems} remaining)
                                </Box>
                            )}
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'rgba(224, 177, 241, 0.7)',
                                    mt: 1,
                                    fontSize: '0.75rem'
                                }}
                            >
                                Showing {visibleItems} of {displayedVenues.length} venues
                            </Typography>
                        </Box>
                    )}

                    {/* Alternate Venues Section - now inside the scrollable container */}
                    {venues.length < 10 && alternateVenues.length > 0 && (
                        <Box sx={{
                            mt: 6,
                            mb: 3,
                            borderTop: '1px solid rgba(255, 255, 255, 0.12)',
                            pt: 4
                        }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#C0C0DD',
                                    fontFamily: 'Inter',
                                    fontWeight: 500,
                                    mb: 3
                                }}
                            >
                                Not many venues match your search. Here are some other available options you might like:
                            </Typography>
                            <Grid container spacing={{ xs: 2.0, md: 2.8 }} paddingRight={0}>
                                {renderAlternateVenueCards()}
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default VenueCardList; 